/**
 * Sidebar Toggle Script
 * 
 * Purpose: This script manages the toggle state of the dashboard sidebar, allowing users to expand or collapse it.
 * It remembers the user's preference by storing the state in localStorage and applies it on page reload.
 * 
 * Features:
 * - Persists sidebar state across page reloads using localStorage
 * - Updates accessibility attributes (aria-expanded)
 * - Toggles Bootstrap 'show' class for visibility
 * - Handles multiple toggle elements
 * - Defaults to 'open' state if no preference is stored
 * 
 * Usage: Include this script in pages with sidebar toggle functionality.
 * Ensure toggle elements have data-sidebar-toggle-value attribute and data-bs-target pointing to sidebar elements.
 */

(() => {
    'use strict';

    // Configuration constants
    const ATTRIBUTE_NAME = 'data-sidebar-toggle-value';
    const ATTRIBUTE_EXPANDED_NAME = 'aria-expanded';
    const ATTRIBUTE_TARGET_NAME = 'data-bs-target';
    const ATTRIBUTE_SHOW_CLASS = 'show';

    const STORAGE_KEY = 'app-sidebar-toggle';

    const DEFAULT_STATE = 'open';
    const DEFAULT_BS_TARGET = '.sidebar-menu-nav';

    // Check if localStorage is available
    const isLocalStorageAvailable = () => {
        try {
            const test = '__localStorage_test__';
            localStorage.setItem(test, test);
            localStorage.removeItem(test);
            return true;
        } catch {
            return false;
        }
    };

    // Retrieve the stored sidebar toggle state from localStorage
    const getStoredSidebarToggle = () => {
        if (!isLocalStorageAvailable()) return null;
        return localStorage.getItem(STORAGE_KEY);
    };

    // Store the sidebar toggle state in localStorage
    const setStoredSidebarToggle = (sidebar) => {
        if (!isLocalStorageAvailable()) return;
        localStorage.setItem(STORAGE_KEY, sidebar);
    };

    // Get the preferred sidebar toggle state: from storage or default
    const getPreferredSidebarToggle = () => {
        const sidebar = getStoredSidebarToggle();
        return sidebar || DEFAULT_STATE;
    };

    // Apply the sidebar toggle state to all relevant elements
    const applySidebarState = (state) => {
        const isExpanded = state === 'open';
        const expandedValue = isExpanded ? 'true' : 'false';

        // Update all toggle elements
        document.querySelectorAll(`[${ATTRIBUTE_NAME}]`).forEach(toggle => {
            toggle.setAttribute(ATTRIBUTE_NAME, state);
            toggle.setAttribute(ATTRIBUTE_EXPANDED_NAME, expandedValue);

            const targetSelector = toggle.getAttribute(ATTRIBUTE_TARGET_NAME);
            if (targetSelector) {
                // Toggle the 'show' class on target elements
                toggleShowClassOnTargetElements(targetSelector, isExpanded);
            }
        });
    };
    const toggleShowClassOnTargetElements = (targetSelector, isExpanded) => {
        document.querySelectorAll(targetSelector).forEach(target => {
            if (isExpanded && !target.classList.contains(ATTRIBUTE_SHOW_CLASS)) {
                target.classList.add(ATTRIBUTE_SHOW_CLASS);
            } else if (!isExpanded && target.classList.contains(ATTRIBUTE_SHOW_CLASS)) {
                target.classList.remove(ATTRIBUTE_SHOW_CLASS);
            }
        });
    };
    // Toggle the sidebar state
    const toggleSidebar = () => {
        const currentState = getStoredSidebarToggle() || DEFAULT_STATE;
        const newState = currentState === 'open' ? 'closed' : 'open';
        setStoredSidebarToggle(newState);
        applySidebarState(newState);
    };

    // Initialize the sidebar on page load
    const initializeSidebar = () => {
        const preferredState = getPreferredSidebarToggle();
        applySidebarState(preferredState);
    };

    // Initialize immediately if DOM is ready, otherwise wait
    if (document.readyState === 'loading') {
        // If the DOM is still loading, apply the preferred state immediately to avoid flickering
        const isExpanded = getPreferredSidebarToggle() === 'open';
        toggleShowClassOnTargetElements(DEFAULT_BS_TARGET, isExpanded);
        //console.log(`first load sidebar expand: ${isExpanded}`);

        // Add event listener to initialize the sidebar once the DOM is fully loaded
        document.addEventListener('DOMContentLoaded', initializeSidebar);
    } else {
        initializeSidebar();
    }

    // Add event listeners using event delegation for better performance
    document.addEventListener('click', (event) => {
        const toggle = event.target.closest(`[${ATTRIBUTE_NAME}]`);
        if (toggle) {
            event.preventDefault();
            toggleSidebar();
        }
    });

})();